import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  Box,
  Typography,
  TextField,
  IconButton,
  Avatar,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function SupportChat() {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchMessages();
    markAsRead();
    pollIntervalRef.current = setInterval(pollMessages, 10000);
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  const pollMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/support/messages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(Array.isArray(res.data.messages) ? res.data.messages : []);
      markAsRead();
    } catch (err) {
      console.error('Poll error:', err);
    }
  };

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/support/messages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(Array.isArray(res.data.messages) ? res.data.messages : []);
      setLoading(false);
      setTimeout(scrollToBottom, 100);
    } catch (err) {
      console.error('Fetch messages error:', err);
      setMessages([]);
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const markAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE}/support/mark-read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleSend = async () => {
    if (!text.trim() && attachments.length === 0) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE}/support/messages`,
        { text: text.trim(), attachments },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setText('');
      setAttachments([]);
      await fetchMessages();
      setTimeout(scrollToBottom, 100);
    } catch (err) {
      console.error('Send error:', err);
      alert('Не удалось отправить сообщение');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      alert('Неверный формат файла.\n\nРазрешены только:\n- JPG/JPEG изображения\n- PNG изображения\n- GIF анимации\n- PDF документы');
      e.target.value = '';
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert('Файл слишком большой.\n\nМаксимальный размер: 10 МБ\nРазмер вашего файла: ' + (file.size / 1024 / 1024).toFixed(2) + ' МБ');
      e.target.value = '';
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_BASE}/support/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      setAttachments([...attachments, res.data]);
      e.target.value = '';
    } catch (err) {
      console.error('Upload error:', err);
      const errorMsg = err.response?.data?.error || 'Не удалось загрузить файл';
      alert(errorMsg);
      e.target.value = '';
    }
  };

  const removeAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const renderAttachment = (att) => {
    const baseUrl = API_BASE.replace('/api', '');
    const fileName = att.url.split('/').pop();
    return (
      <Chip
        label={fileName}
        component="a"
        href={`${baseUrl}${att.url}`}
        target="_blank"
        rel="noopener noreferrer"
        clickable
        size="small"
        sx={{ mr: 0.5, mb: 0.5 }}
      />
    );
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return 'Сегодня';
    if (date.toDateString() === yesterday.toDateString()) return 'Вчера';
    return date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
    });
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  const groupedMessages = (messages || []).reduce((groups, message) => {
    const date = formatDate(message.createdAt);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {});

  return (
    <Container maxWidth="md" sx={{ mt: { xs: 1, md: 2 }, mb: { xs: 2, md: 4 }, px: { xs: 0, sm: 2 } }}>
      <Paper
        elevation={0}
        sx={{
          height: { xs: 'calc(100vh - 140px)', md: 'calc(100vh - 200px)' },
          display: 'flex',
          flexDirection: 'column',
          borderRadius: { xs: '16px', md: '24px' },
          overflow: 'hidden',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(0, 0, 0, 0.08)',
        }}
      >
        <Box
          sx={{
            p: 2,
            background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
            color: '#fff',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.3)' }}>Т</Avatar>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Техподдержка ThinkLink
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Обычно отвечаем в течение часа
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 2,
            background: '#f7fafc',
          }}
        >
          {Object.entries(groupedMessages).map(([date, dateMessages]) => (
            <Box key={date}>
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                <Chip label={date} size="small" sx={{ bgcolor: 'rgba(0, 0, 0, 0.08)' }} />
              </Box>

              {dateMessages.map((message, idx) => {
                const isClient = message.senderRole === 'client';

                return (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex',
                      justifyContent: isClient ? 'flex-end' : 'flex-start',
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{
                        maxWidth: '70%',
                        display: 'flex',
                        flexDirection: isClient ? 'row-reverse' : 'row',
                        gap: 1,
                        alignItems: 'flex-end',
                      }}
                    >
                      {!isClient && (
                        <Avatar sx={{ width: 32, height: 32, bgcolor: '#cbd5e0' }}>
                          Т
                        </Avatar>
                      )}

                      <Box>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 1.5,
                            borderRadius: isClient ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                            background: isClient
                              ? 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)'
                              : '#d1d5db',
                            color: isClient ? '#fff' : '#2d3748',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                          }}
                        >
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {message.text}
                          </Typography>

                          {message.attachments && message.attachments.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                              {message.attachments.map((att, attIdx) => (
                                <Box key={attIdx}>{renderAttachment(att)}</Box>
                              ))}
                            </Box>
                          )}
                        </Paper>

                        <Typography
                          variant="caption"
                          sx={{
                            color: '#a0aec0',
                            display: 'block',
                            mt: 0.5,
                            textAlign: isClient ? 'right' : 'left',
                          }}
                        >
                          {formatTime(message.createdAt)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ))}

          <div ref={messagesEndRef} />
        </Box>

        <Box sx={{ p: 2, background: '#fff', borderTop: '1px solid rgba(0, 0, 0, 0.08)' }}>
          {attachments.length > 0 && (
            <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {attachments.map((att, idx) => (
                <Chip
                  key={idx}
                  label={att.url.split('/').pop()}
                  onDelete={() => removeAttachment(idx)}
                  size="small"
                  deleteIcon={<CloseIcon />}
                />
              ))}
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <input
              ref={fileInputRef}
              type="file"
              hidden
              accept="image/jpeg,image/png,image/gif,application/pdf"
              onChange={handleFileUpload}
            />
            <IconButton
              onClick={() => fileInputRef.current?.click()}
              sx={{ color: '#718096' }}
            >
              <AttachFileIcon />
            </IconButton>

            <TextField
              fullWidth
              multiline
              maxRows={4}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Напишите сообщение..."
              variant="outlined"
              size="small"
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '20px',
                  bgcolor: '#f7fafc',
                },
              }}
            />

            <IconButton
              onClick={handleSend}
              disabled={!text.trim() && attachments.length === 0}
              sx={{
                background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                color: '#fff',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0984e3 0%, #74b9ff 100%)',
                },
                '&.Mui-disabled': {
                  background: '#e2e8f0',
                  color: '#a0aec0',
                },
              }}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
}

export default SupportChat;
